import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import * as LucideIcons from 'lucide-react';
import { marked } from 'marked';
import React, { useState } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import Modal from '../../../common/Modal';
import ModalForm from '../../../common/ModalForm';
import { showToast } from '../../../common/ToastHandler';
import MetricBlock from '../../../renderer/blocks/MetricBlock';
import { ActionCellProps } from '../types';

const ActionCell: React.FC<ActionCellProps> = ({
  column,
  rowData,
  onDataReload,
  tableId,
}) => {
  const { getIdTokenClaims } = useAuth0();
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [confirmationInput, setConfirmationInput] = useState<string>('');
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [currentAction, setCurrentAction] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<any>(null);

  const handleConfirmationSubmit = async (): Promise<void> => {
    if (currentAction.input_placeholder) {
      // Extract expected confirmation text from placeholder
      // Pattern: "Type 'WORD' to confirm..."
      const match = currentAction.input_placeholder.match(
        /Type '([^']+)' to confirm/i
      );
      const expectedText = match ? match[1].toLowerCase() : null;

      if (expectedText === 'update') {
        // For update actions, check if role is selected
        if (!selectedRole) {
          alert('Please select a role');
          return;
        }
      } else if (
        expectedText &&
        confirmationInput.toLowerCase() !== expectedText
      ) {
        alert(`Please type "${expectedText}" to confirm`);
        return;
      }
    }

    await executeAction(currentAction);
    setShowConfirmDialog(false);
    setConfirmationInput('');
    setSelectedRole('');
    setCurrentAction(null);
  };

  const handleActionClick = async (action: any): Promise<void> => {
    if (isLoading) return; // Prevent multiple clicks during loading

    if (action.modal_title) {
      setCurrentAction(action);
      // Pre-select current role if this is an update action
      const match = action.input_placeholder?.match(
        /Type '([^']+)' to confirm/i
      );
      const expectedText = match ? match[1].toLowerCase() : null;
      if (expectedText === 'update' && rowData.role) {
        setSelectedRole(rowData.role);
      }
      setShowConfirmDialog(true);
    } else {
      await executeAction(action);
    }
  };

  const handleComplexIconClick = (iconConfig: any): void => {
    if (iconConfig.modal_display) {
      // Replace user_id placeholders in modal content if needed
      const processedModalContent = { ...iconConfig.modal_display };
      if (
        processedModalContent.content &&
        processedModalContent.content.submit_action
      ) {
        const submitAction = processedModalContent.content.submit_action;
        if (submitAction.url) {
          submitAction.url = submitAction.url.replace(
            /\{([^}]+)\}/g,
            (_, key) => {
              if (key === 'user_id') {
                return rowData['email'] || rowData[key] || '';
              }
              return rowData[key] || '';
            }
          );
        }
      }
      setModalContent(processedModalContent);
      setIsModalOpen(true);
    } else if (iconConfig.action) {
      handleActionClick(iconConfig.action);
    }
  };

  const renderModalContent = (): JSX.Element | null => {
    if (!modalContent) return null;

    // Handle array of content blocks
    if (Array.isArray(modalContent.content)) {
      return (
        <div className="modal-content-blocks">
          {modalContent.content.map((contentBlock, index) => (
            <div key={index} className="modal-content-block">
              {renderSingleContentBlock(contentBlock)}
            </div>
          ))}
        </div>
      );
    }

    // Handle single content block
    return renderSingleContentBlock(modalContent.content);
  };

  const renderSingleContentBlock = (contentBlock: any): JSX.Element | null => {
    if (!contentBlock) return null;

    switch (contentBlock.kind) {
      case 'form':
        return (
          <ModalForm
            fields={contentBlock.fields}
            submitAction={contentBlock.submit_action}
            rowData={rowData}
            onSubmit={formData => {
              if (contentBlock.submit_action.response.clear_form) {
                setIsModalOpen(false);
                setModalContent(null);
              }
            }}
          />
        );
      case 'text':
        return (
          <div
            className="modal-text-content"
            dangerouslySetInnerHTML={{
              __html:
                contentBlock.content !== ''
                  ? String(marked.parse(contentBlock.content))
                  : '',
            }}
            style={{
              lineHeight: '1.6',
              fontSize: '14px',
              color: '#333',
            }}
          />
        );
      case 'metric':
        return <MetricBlock block={contentBlock} />;
      default:
        return null;
    }
  };

  const executeAction = async (action: any): Promise<void> => {
    setIsLoading(true);
    showToast('info', 'Processing your request...', 2000);

    // Replace template variables in URL and body
    const url = action.url.replace(/\{([^}]+)\}/g, (_, key) => {
      // Special handling for user_id - use email address instead
      if (key === 'user_id') {
        return rowData['email'] || rowData[key] || '';
      }
      return rowData[key] || '';
    });

    try {
      const getidToken = await getIdTokenClaims();
      const idToken = getidToken.__raw;

      // Check if this is a file download action
      const isDownloadAction =
        action.method === 'GET' &&
        (url.includes('action=download') ||
          action.url.includes('action=download') ||
          action.type === 'download');

      if (isDownloadAction) {
        // Handle file download - first get the presigned URL
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json',
            ...(action.headers || {}),
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const responseData = await response.json();

        // Check if we got a presigned_url in the response
        if (responseData.presigned_url) {
          // Use the presigned URL to download the file
          const link = document.createElement('a');
          link.href = responseData.presigned_url;
          link.setAttribute('download', responseData.filename || 'download');
          link.style.display = 'none';

          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Dismiss progress toast and show success
          toast.dismiss();
          showToast(
            'success',
            `File "${responseData.filename || 'file'}" downloaded successfully`
          );
        } else {
          // Fallback: treat response as direct file data
          const blob = await response.blob();
          const downloadUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = downloadUrl;

          // Try to get filename from Content-Disposition header
          const contentDisposition = response.headers.get(
            'Content-Disposition'
          );
          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="(.+)"/);
            if (filenameMatch) {
              link.download = filenameMatch[1];
            }
          }

          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(downloadUrl);

          // Dismiss progress toast and show success
          toast.dismiss();
          showToast('success', 'File downloaded successfully');
        }

        setIsLoading(false);
        return;
      }

      let body = action.body;
      if (body) {
        try {
          body = JSON.parse(body);

          Object.keys(body).forEach(key => {
            const value = body[key];
            if (typeof value === 'string' && value.includes('{')) {
              const match = value.match(/\{([^}]+)\}/);
              if (match && match[1]) {
                const row_data_key = match[1];
                // Special handling for role field in update actions
                if (row_data_key === 'role' && selectedRole) {
                  body[key] = selectedRole;
                } else if (row_data_key === 'user_id') {
                  // Special handling for user_id - use email address instead
                  body[key] = rowData['email'] || rowData[row_data_key] || '';
                } else {
                  body[key] = rowData[row_data_key] || '';
                }
              }
            }
          });

          body = JSON.stringify(body);
        } catch (error) {
          console.error('Failed to parse action body:', error);
          console.error('Failed at body value:', body);
          toast.dismiss(); // Dismiss all progress toasts
          showToast('error', 'Failed to process request data');
          setIsLoading(false);
          return;
        }
      }

      const response = await axios({
        method: action.method,
        url: url,
        data: body,
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
          ...(action.headers || {}),
        },
      });

      // Dismiss progress toast
      toast.dismiss();

      const status_handler =
        action.response?.status_handlers?.find(
          handler => handler.status_code === response.status
        ) || action.response?.default_handler;

      if (status_handler) {
        if (status_handler.show_toast) {
          showToast(
            status_handler.toast_type || 'success',
            status_handler.message
          );
        }

        if (status_handler.redirect_url) {
          // Process redirect URL to handle user_id placeholders
          const processedRedirectUrl = status_handler.redirect_url.replace(
            /\{([^}]+)\}/g,
            (_, key) => {
              if (key === 'user_id') {
                return rowData['email'] || rowData[key] || '';
              }
              return rowData[key] || '';
            }
          );
          window.location.href = processedRedirectUrl;
        }
      } else {
        // Show success message if no specific handler
        showToast('success', 'Action completed successfully');
      }

      // Check for refresh/reload at the response level
      if (action.response?.refresh_page || action.response?.reload_data) {
        if (onDataReload) {
          onDataReload();
        } else {
          window.location.reload();
        }
      }
    } catch (error) {
      console.error('Action failed:', error);
      console.log('Error details:', {
        status: error.response?.status,
        url: url,
        urlIncludes: url.includes('/v3/deal_room/refresh_tabs'),
      });

      // Check if error is 503 Service Unavailable for refresh_tabs endpoint
      if (
        error.response &&
        error.response.status === 503 &&
        url.includes('/v3/deal_room/refresh_tabs')
      ) {
        // Don't dismiss progress toast immediately, let it transition to info toast
        setTimeout(() => {
          toast.dismiss();
          showToast(
            'info',
            'Your request is being processed in the background. Please wait a moment and refresh to see updates.'
          );
        }, 100);
        // Close the modal since the request is processing in background
        setShowConfirmDialog(false);
        setConfirmationInput('');
        setSelectedRole('');
        setCurrentAction(null);
      } else {
        // Dismiss progress toast for other errors
        toast.dismiss();
        showToast('error', 'Action failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const renderIcon = (
    icon_config: any,
    action: any,
    tooltip?: string
  ): JSX.Element | null => {
    if (!icon_config || !icon_config.value) {
      console.log('Invalid icon config:', icon_config);
      return null;
    }

    const icon_value = icon_config.value.toLowerCase();

    // Deal Rooms: Only show delete action if backend says actions === "delete" and only for the deal rooms table
    if (
      (column?.key || '').toLowerCase() === 'actions' &&
      icon_value === 'delete'
    ) {
      if ((tableId || '').toLowerCase() === 'deal_rooms_table') {
        const rowActions = String(rowData?.actions || '')
          .trim()
          .toLowerCase();
        if (rowActions !== 'delete') {
          return null;
        }
      }
    }

    // Deal Rooms: Only show remove membership action if backend says actions === "remove_membership" and only for the deal rooms table
    if (
      (column?.key || '').toLowerCase() === 'actions' &&
      icon_value === 'user-minus'
    ) {
      if ((tableId || '').toLowerCase() === 'deal_rooms_table') {
        const rowActions = String(rowData?.actions || '')
          .trim()
          .toLowerCase();
        if (rowActions !== 'remove_membership') {
          return null;
        }
      }
    }

    const icon_map = {
      delete: 'Trash2',
      edit: 'Edit',
      view: 'Eye',
      download: 'Download',
      upload: 'Upload',
      settings: 'Settings',
      add: 'Plus',
      remove: 'Minus',
      search: 'Search',
      filter: 'Filter',
      sort: 'ArrowUpDown',
      close: 'X',
      check: 'Check',
      warning: 'AlertTriangle',
      info: 'Info',
      success: 'CheckCircle',
      error: 'XCircle',
      'trash-2': 'Trash2',
      'user-plus': 'UserPlus',
      'user-minus': 'UserMinus',
    };

    const icon_name =
      icon_map[icon_value] ||
      icon_value
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join('');

    const IconComponent = LucideIcons[icon_name];

    if (!IconComponent) {
      console.error(
        `Icon not found: ${icon_config.value} (tried ${icon_name}). Available icons:`,
        Object.keys(LucideIcons)
      );
      return null;
    }

    return (
      <button
        onClick={() => handleActionClick(action)}
        className={`action-icon-button ${isLoading ? 'disabled' : ''}`}
        title={tooltip}
        disabled={isLoading}
        style={{
          opacity: isLoading ? 0.6 : 1,
        }}
      >
        {isLoading ? (
          <LucideIcons.Loader2
            size={icon_config.size === 'small' ? 16 : 24}
            className="animate-spin"
          />
        ) : (
          <IconComponent
            size={icon_config.size === 'small' ? 16 : 24}
            color={icon_config.color || 'currentColor'}
          />
        )}
      </button>
    );
  };

  const renderComplexIcon = (
    iconConfig: any,
    index: number
  ): JSX.Element | null => {
    if (!iconConfig || !iconConfig.value) {
      console.log('Invalid complex icon config:', iconConfig);
      return null;
    }

    const icon_value = iconConfig.value.toLowerCase();

    // Deal Rooms: Only show delete action if backend says actions === "delete" and only for the deal rooms table
    if (
      (column?.key || '').toLowerCase() === 'actions' &&
      icon_value === 'delete'
    ) {
      if ((tableId || '').toLowerCase() === 'deal_rooms_table') {
        const rowActions = String(rowData?.actions || '')
          .trim()
          .toLowerCase();
        if (rowActions !== 'delete') {
          return null;
        }
      }
    }

    const icon_map = {
      delete: 'Trash2',
      edit: 'Edit',
      view: 'Eye',
      download: 'Download',
      upload: 'Upload',
      settings: 'Settings',
      add: 'Plus',
      remove: 'Minus',
      search: 'Search',
      filter: 'Filter',
      sort: 'ArrowUpDown',
      close: 'X',
      check: 'Check',
      warning: 'AlertTriangle',
      info: 'Info',
      success: 'CheckCircle',
      error: 'XCircle',
      'trash-2': 'Trash2',
      'user-plus': 'UserPlus',
      'user-minus': 'UserMinus',
    };

    const icon_name =
      icon_map[icon_value] ||
      icon_value
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join('');

    const IconComponent = LucideIcons[icon_name];

    if (!IconComponent) {
      console.error(
        `Icon not found: ${iconConfig.value} (tried ${icon_name}). Available icons:`,
        Object.keys(LucideIcons)
      );
      return null;
    }

    return (
      <button
        key={index}
        onClick={() => handleComplexIconClick(iconConfig)}
        className={`action-icon-button ${isLoading ? 'disabled' : ''}`}
        title={iconConfig.tooltip}
        disabled={isLoading}
        style={{
          opacity: isLoading ? 0.6 : 1,
        }}
      >
        {isLoading ? (
          <LucideIcons.Loader2
            size={iconConfig.size === 'small' ? 16 : 24}
            className="animate-spin"
          />
        ) : (
          <IconComponent
            size={iconConfig.size === 'small' ? 16 : 24}
            color={iconConfig.color || 'currentColor'}
          />
        )}
      </button>
    );
  };

  // Handle complex_icon type
  if (column.type_ === 'complex_icon') {
    return (
      <>
        <div className="action-icons-container">
          {column.complex_icon &&
            column.complex_icon.map((iconConfig, index) =>
              renderComplexIcon(iconConfig, index)
            )}
        </div>

        {/* Modal for complex icon */}
        {modalContent && (
          <Modal
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              setModalContent(null);
            }}
            title={modalContent.modal_title}
            content={renderModalContent()}
            size={modalContent.size}
            hasCloseButton={modalContent.has_close_button}
          />
        )}
      </>
    );
  }

  // Handle regular icon type
  if (column.type_ !== 'icon' || !Array.isArray(column.sub_keys)) {
    console.log('Invalid column config:', {
      type: column.type_,
      has_sub_keys: Array.isArray(column.sub_keys),
    });
    return null;
  }

  return (
    <>
      <div className="action-icons-container">
        {column.sub_keys.map((sub_key, index) => {
          const icon_config = sub_key.icon;
          if (!icon_config) {
            console.log('No icon config for sub_key:', sub_key.id);
            return null;
          }

          const tooltip = sub_key.label || sub_key.display_rules?.[0]?.tooltip;

          return (
            <div key={index} className="action-icon-wrapper" title={tooltip}>
              {renderIcon(icon_config, (icon_config as any).action, tooltip)}
            </div>
          );
        })}
      </div>

      {showConfirmDialog && currentAction && (
        <div className="modal-overlay">
          <div className="modal-content direct-overlay">
            <h3>{currentAction.modal_title}</h3>
            {currentAction.input_placeholder &&
              (() => {
                const match = currentAction.input_placeholder.match(
                  /Type '([^']+)' to confirm/i
                );
                const expectedText = match ? match[1].toLowerCase() : null;

                if (expectedText === 'update') {
                  const roleOptions = [
                    { value: 'collaborator', label: 'Collaborator' },
                    { value: 'viewer', label: 'Viewer' },
                  ];

                  return (
                    <Select
                      value={
                        roleOptions.find(
                          option => option.value === selectedRole
                        ) || null
                      }
                      onChange={selectedOption =>
                        setSelectedRole(
                          selectedOption ? selectedOption.value : ''
                        )
                      }
                      options={roleOptions}
                      placeholder="Select a role"
                      className="role-select"
                      classNamePrefix="select"
                      isClearable
                      isDisabled={isLoading}
                    />
                  );
                } else {
                  return (
                    <input
                      type="text"
                      placeholder={currentAction.input_placeholder}
                      value={confirmationInput}
                      onChange={e => setConfirmationInput(e.target.value)}
                      className="confirmation-input"
                      disabled={isLoading}
                    />
                  );
                }
              })()}
            <div className="modal-actions">
              <button
                onClick={() => {
                  setShowConfirmDialog(false);
                  setConfirmationInput('');
                  setSelectedRole('');
                  setCurrentAction(null);
                }}
                className={`cancel-button ${isLoading ? 'disabled' : ''}`}
                disabled={isLoading}
              >
                {currentAction.cancel_label || 'Cancel'}
              </button>
              <button
                onClick={handleConfirmationSubmit}
                className={(() => {
                  const match = currentAction.input_placeholder?.match(
                    /Type '([^']+)' to confirm/i
                  );
                  const expectedText = match ? match[1].toLowerCase() : null;
                  const baseClass =
                    expectedText === 'update'
                      ? 'update-action-button'
                      : 'confirm-button';
                  return `${baseClass} ${isLoading ? 'disabled' : ''}`;
                })()}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <LucideIcons.Loader2 size={16} className="animate-spin" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <span>{currentAction.confirm_label || 'Confirm'}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ActionCell;
